import { Trans, useLingui } from '@lingui/react/macro';
import {
  Copy,
  Download,
  Edit,
  Eye,
  MoreHorizontal,
  RotateCcw,
  Send,
  ToggleLeft,
  ToggleRight,
  Trash2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { SavedTemplate } from '@/types/saved-template';

/**
 * Per-row actions for the templates table (Task 6.11, L7–L14). Renders
 * an inline Preview button (so users don't have to open the dropdown
 * to see a template, matching v1) plus a dropdown of secondary actions.
 *
 * Actions swap based on `status`: drafts get Publish, published
 * templates get Unpublish, and deleted templates get Restore (no
 * Publish or Unpublish). The handler set is deliberately wide
 * (one per action) so the parent can wire each one to the right
 * mutation hook without this component needing to know about
 * mutation state.
 */
export function TemplatesRowActions<Template extends RowTemplate>(
  props: TemplatesRowActionsProps<Template>,
) {
  const { t } = useLingui();
  const { template } = props;
  // Dropdown actions are gated by lifecycle status (Preview stays inline,
  // read-only, for every status):
  //   - draft     → Edit, Duplicate, Publish, Export, Delete
  //                 (no Apply to rota — a draft isn't ready to be used)
  //   - published → Edit, Duplicate, Apply to rota, Unpublish, Export, Delete
  //   - deleted   → Export, Restore
  const isDraft = template.status === 'draft';
  const isPublished = template.status === 'published';
  const isDeleted = template.status === 'deleted';
  // A template with no shifts has nothing to preview, apply, or export.
  const hasShifts = template.shifts.length > 0;
  return (
    <div className="flex items-center justify-end gap-1">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => props.onPreview(template)}
        disabled={!hasShifts}
      >
        <Eye className="mr-1 h-4 w-4" /> <Trans>Preview</Trans>
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label={t`Actions`}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {!isDeleted && (
            <>
              <DropdownMenuItem onSelect={() => props.onEdit(template)}>
                <Edit className="mr-2 h-4 w-4" /> <Trans>Edit</Trans>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => props.onDuplicate(template)}>
                <Copy className="mr-2 h-4 w-4" /> <Trans>Duplicate</Trans>
              </DropdownMenuItem>
              {isPublished && (
                <DropdownMenuItem
                  disabled={!hasShifts}
                  onSelect={() => props.onApplyToRota(template)}
                >
                  <Send className="mr-2 h-4 w-4" /> <Trans>Apply to rota</Trans>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
            </>
          )}
          {isDraft && (
            <DropdownMenuItem onSelect={() => props.onPublish(template)}>
              <ToggleRight className="mr-2 h-4 w-4" /> <Trans>Publish</Trans>
            </DropdownMenuItem>
          )}
          {isPublished && (
            <DropdownMenuItem onSelect={() => props.onUnpublish(template)}>
              <ToggleLeft className="mr-2 h-4 w-4" /> <Trans>Unpublish</Trans>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem disabled={!hasShifts} onSelect={() => props.onExport(template)}>
            <Download className="mr-2 h-4 w-4" /> <Trans>Export to Excel</Trans>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {isDeleted ? (
            <DropdownMenuItem onSelect={() => props.onRestore(template)}>
              <RotateCcw className="mr-2 h-4 w-4" /> <Trans>Restore</Trans>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onSelect={() => props.onDelete(template)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" /> <Trans>Delete</Trans>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

type RowTemplate = Pick<SavedTemplate, 'id' | 'name' | 'status' | 'shifts'>;

interface TemplatesRowActionsProps<Template extends RowTemplate> {
  template: Template;
  onPreview: (template: Template) => void;
  onEdit: (template: Template) => void;
  onDuplicate: (template: Template) => void;
  onApplyToRota: (template: Template) => void;
  onDelete: (template: Template) => void;
  onPublish: (template: Template) => void;
  onUnpublish: (template: Template) => void;
  onRestore: (template: Template) => void;
  onExport: (template: Template) => void;
}
